import React, { useEffect, useMemo, useState } from "react";
import {
  Typography,
  Divider,
  Box,
  Grid,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Switch,
  FormControlLabel,
  Collapse,
  Paper,
  Chip,
  Button,
  Tooltip,
  TextField
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Settings as SettingsIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ColorLens as ColorLensIcon,
  CropRotate as CropRotateIcon
} from "@mui/icons-material";
import { convertDateToWords } from "../utils/dateToWords";
import { useRef } from "react";

const toCamelCase = (str) => {
  if (!str) return "";
  return str
    ?.toLowerCase()
    .replace(/[^a-zA-Z0-9 ]/g, "")
    .split(" ")
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(" ");
};

// Student Info Fields Configuration
const STUDENT_INFO_FIELDS = {
  photo: { label: "Photo", defaultVisible: true, column: 3 },
  name: { label: "Student's Name", defaultVisible: true, column: 1 },
  fullname: { label: "Student's full Name", defaultVisible: true, column: 1 },
  father: { label: "Father's name", defaultVisible: true, column: 1 },
  mother: { label: "Mother's name", defaultVisible: true, column: 1 },
  apaarId: { label: "Apaar ID", defaultVisible: true, column: 1 },
  aadhaarNumber: { label: "Aadhaar Number", defaultVisible: false, column: 2 },
  studied: { label: "Studied (Class)", defaultVisible: true, column: 1 },
  casteCategory: { label: "Category/Caste", defaultVisible: false, column: 2 },
  standard: { label: "Standard", defaultVisible: false, column: 1 },
  dob: { label: "Date of Birth", defaultVisible: true, column: 2 },
  dobInWords: { label: "DOB (In Words)", defaultVisible: false, column: 2 },
  heightWeight: { label: "Height/Weight", defaultVisible: false, column: 2 },
  // contact: { label: "Contact Number", defaultVisible: false, column: 2 },
  address: { label: "Address", defaultVisible: false, column: 2 },
  rollNo: { label: "Roll No", defaultVisible: false, column: 2 },
  // examType: { label: "Exam Type", defaultVisible: true, column: 1 },
  // academicYear: { label: "Academic Year", defaultVisible: true, column: 1 },
  // remarks: { label: "Remarks", defaultVisible: true, column: 2 },
};

const OneTermMarksheetReceipt = ({ receiptData, school, setTemID }) => {
  const students = receiptData.data || [];
  const receiptId = `marksheet`;
  const hasFetched = useRef(false);

  // --- Customization controls ---
  const [themeColor, setThemeColor] = useState("#1976d2");
  const [selectedTerm, setSelectedTerm] = useState("all");
  const [showSettings, setShowSettings] = useState(false);
  const [isLandscape, setIsLandscape] = useState(false);

  const [fieldVisibility, setFieldVisibility] = useState(() => {
    const saved = localStorage.getItem('marksheetFieldVisibility');
    if (saved) {
      return JSON.parse(saved);
    }
    const defaults = {};
    Object.entries(STUDENT_INFO_FIELDS).forEach(([key, field]) => {
      defaults[key] = field.defaultVisible;
    });
    return defaults;
  });

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    if (setTemID) {
      setTemID(receiptId);
    }
    console.log(students);
  }, [receiptData, setTemID, receiptId]);

  // Save visibility settings
  useEffect(() => {
    localStorage.setItem('marksheetFieldVisibility', JSON.stringify(fieldVisibility));
  }, [fieldVisibility]);

  // Add landscape styles to document
  useEffect(() => {
    if (isLandscape) {
      const style = document.createElement('style');
      style.id = 'landscape-style-one-term';
      style.innerHTML = `
        @media print {
          @page {
            size: landscape;
            margin: 8mm;
          }
          .pagebreak {
            page-break-after: always;
          }
        }
        .landscape-container {
          max-width: 1100px !important;
        }
        .portrait-container {
          max-width: 800px !important;
        }
        .landscape-table th, .landscape-table td {
          padding: 4px 6px !important;
          font-size: 0.75rem !important;
        }
      `;
      document.head.appendChild(style);
    } else {
      const existingStyle = document.getElementById('landscape-style-one-term');
      if (existingStyle) {
        existingStyle.remove();
      }
    }
    return () => {
      const existingStyle = document.getElementById('landscape-style-one-term');
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, [isLandscape]);

  // Discover exam types
  const availableTerms = useMemo(() => {
    const set = new Set(
      students.map((s) => s.examType?.trim()).filter(Boolean)
    );
    return Array.from(set);
  }, [students]);

  // Filter students by selected term
  const filteredStudents = useMemo(() => {
    if (selectedTerm === "all") return students;
    return students.filter(
      (s) => s.examType?.trim().toLowerCase() === selectedTerm.trim().toLowerCase()
    );
  }, [students, selectedTerm]);

  // Toggle field visibility
  const toggleFieldVisibility = (fieldKey) => {
    setFieldVisibility(prev => ({
      ...prev,
      [fieldKey]: !prev[fieldKey]
    }));
  };

  // Toggle all fields
  const toggleAllFields = (visible) => {
    const newState = {};
    Object.keys(STUDENT_INFO_FIELDS).forEach(key => {
      newState[key] = visible;
    });
    setFieldVisibility(newState);
  };

  const toggleLandscape = () => {
    setIsLandscape(!isLandscape);
  };

  // Get visibility count
  const visibleCount = Object.values(fieldVisibility).filter(v => v).length;
  const totalFields = Object.keys(STUDENT_INFO_FIELDS).length;

  const borderColor = themeColor;
  const headerBg = alpha(themeColor, 0.14);
  const totalsBg = alpha(themeColor, 0.06);

  // Dynamic styles based on landscape mode
  const containerMaxWidth = isLandscape ? "1100px" : "800px";
  const cellPadding = isLandscape ? "4px" : "8px";
  const fontSize = isLandscape ? "0.7rem" : "0.85rem";
  // const headerFontSize = isLandscape ? "0.8rem" : "1rem";

  const thStyle = {
    border: `1px solid ${borderColor}`,
    padding: cellPadding,
    background: headerBg,
    fontSize: fontSize
  };
  const tdStyle = {
    border: `1px solid ${borderColor}`,
    padding: cellPadding,
    fontSize: fontSize
  };

  // Get student info with proper fallbacks
  const getStudentInfo = (ms) => {
    const marks = ms.marks || [];
    const totalMax = marks.reduce((sum, m) => sum + Number(m.maxMark || 0), 0);
    const totalObt = marks.reduce((sum, m) => sum + Number(m.marksObtained || 0), 0);

    // Find first term data for student info
    const firstTerm = students.find((s) =>
      s.examType?.trim().toLowerCase().includes('first')
    ) || ms;

    return {
      apaarId: firstTerm?.apaarId || ms?.apaarId || '',
      aadhaarNumber: firstTerm?.aadhaar || ms?.aadhaar || '',
      name: toCamelCase(`${firstTerm?.name || ms?.name || ''} ${firstTerm?.last || ms?.last || ''}`.trim()) || '',
      father: toCamelCase(`${firstTerm?.father || ms?.father || ''} ${firstTerm?.last || ms?.last || ''}`.trim()) || '',
      mother: toCamelCase(`${firstTerm?.mother || ms?.mother || ''} ${firstTerm?.last || ms?.last || ''}`.trim()) || '',
      studied: `${ms?.classRoom?.standard || ms?.standard || ''} |  ${ms?.classRoom?.section || ''}`,
      admissionNo: `${ms?.id || ''} | Roll No: ${ms?.rollNumber || ''}`,
      casteCategory: `${ms?.category} | caste: ${ms?.caste || ''}`,
      dob: ms?.birthday || ms?.dateOfBirth ? new Date(ms.birthday || ms.dateOfBirth).toLocaleDateString("en-GB") : '',
      dobInWords: ms?.birthday || ms?.dateOfBirth ? convertDateToWords(ms.birthday || ms.dateOfBirth) : '',
      fullname: toCamelCase(`${firstTerm?.studentName}`.trim()) || '',
      photo: ms?.photo || firstTerm?.photo || null,
      contact: ms?.contact || ms?.mobile || '',
      address: ms?.address || ms?.addressDTO ?
        toCamelCase([ms?.address, ms?.address?.city, ms?.address?.pincode, ms?.address?.state].filter(Boolean).join(", ")) :
        '',
      rollNo: ms?.rollNumber || ms?.id || '',
      totalMarks: `${totalObt}/${totalMax}`,
      percentage: typeof ms.percentage === "number" ? ms.percentage.toFixed(2) : ms.percentage || '',
      grade: ms.grade || '',
      result: ms.result || (totalObt >= totalMax * 0.33 ? "PASS" : "FAIL"),
      remarks: ms.remarks || '',
      examType: toCamelCase(ms?.examType || ''),
      academicYear: ms?.academicYear || '',
      standard: `${ms?.classRoom?.standard || ''} |  ${ms?.classRoom?.section || ''}`,
    };
  };

  // Render student info field based on field key
  const renderStudentField = (fieldKey, studentInfo) => {
    if (!fieldVisibility[fieldKey]) return null;

    const field = STUDENT_INFO_FIELDS[fieldKey];
    let value = studentInfo[fieldKey];

    // Special handling for photo
    // if (fieldKey === 'photo') {
    //   if (!value) return null;
    //   return (
    //     <Box key={fieldKey} display="flex" justifyContent="center" my={1}>
    //       <img
    //         src={value}
    //         alt="Student"
    //         style={{ 
    //           width: isLandscape ? 80 : 100, 
    //           height: isLandscape ? 80 : 100, 
    //           borderRadius: '50%', 
    //           objectFit: 'cover',
    //           border: `2px solid ${borderColor}`
    //         }}
    //         onError={(e) => e.target.style.display = 'none'}
    //       />
    //     </Box>
    //   );
    // }

    // Special handling for height/weight
    if (fieldKey === 'heightWeight') {
      return (
        <Box key={fieldKey} display={'flex'} justifyContent={'space-between'} alignItems="center" sx={{ mb: 0.5 }}>
          <Typography variant="body2" fontWeight={700} sx={{ fontSize: fontSize }}>{field.label}</Typography>
          <TextField
            variant="standard"
            placeholder="Height/Weight"
            value={value}
            size="small"
            slotProps={{
              input: {
                disableUnderline: true,
              },
            }}
          />
        </Box>
      );
    }

    // Skip rendering if value is empty
    if (!value || value === '') return null;

    return (
      <Box
        key={fieldKey}
        sx={{
          display: "grid",
          gridTemplateColumns: isLandscape ? "120px auto" : "140px auto",
          mb: 0.3,
          gap: 0.5,
          alignItems: "center"
        }}
      >
        <Typography sx={{ fontWeight: "bold", fontSize: fontSize }} component="span">
          {field.label} :
        </Typography>
        <Typography sx={{ fontSize: fontSize }} component="span">
          {value}
        </Typography>
      </Box>
    );
  };

  // Get fields for column 1 and column 2
  const getFieldsByColumn = (column) => {
    return Object.keys(STUDENT_INFO_FIELDS)
      .filter(key => STUDENT_INFO_FIELDS[key].column === column)
      .filter(key => fieldVisibility[key]);
  };

  return (
    <Box id={receiptId}>
      {/* Controls Panel */}
      <Paper
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
          alignItems: "center",
          justifyContent: "center",
          maxWidth: containerMaxWidth,
          margin: "auto",
          mb: 2,
          p: 1.5,
          "@media print": { display: "none" },
        }}
        elevation={2}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant="body2" fontWeight={600} fontSize="0.8rem">
            <ColorLensIcon fontSize="small" sx={{ verticalAlign: "middle", mr: 0.5 }} />
            Color:
          </Typography>
          <input
            type="color"
            value={themeColor}
            onChange={(e) => setThemeColor(e.target.value)}
            style={{
              width: 32,
              height: 28,
              padding: 0,
              border: "1px solid #ccc",
              borderRadius: 4,
              cursor: "pointer",
            }}
          />
        </Box>

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel id="term-filter-label" sx={{ fontSize: "0.8rem" }}>Term</InputLabel>
          <Select
            labelId="term-filter-label"
            label="Term"
            value={selectedTerm}
            onChange={(e) => setSelectedTerm(e.target.value)}
            sx={{ fontSize: "0.8rem" }}
          >
            <MenuItem value="all">All Terms</MenuItem>
            {availableTerms.map((t) => (
              <MenuItem key={t} value={t}>
                {toCamelCase(t)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          size="small"
          variant={isLandscape ? "contained" : "outlined"}
          startIcon={<CropRotateIcon />}
          onClick={toggleLandscape}
          sx={{ fontSize: "0.75rem", textTransform: "none" }}
        >
          {isLandscape ? "Landscape" : "Portrait"}
        </Button>

        <Button
          size="small"
          variant="outlined"
          startIcon={<SettingsIcon />}
          onClick={() => setShowSettings(!showSettings)}
          sx={{ fontSize: "0.75rem", textTransform: "none" }}
        >
          Customize
        </Button>

        <Tooltip title={`${visibleCount}/${totalFields} fields visible`}>
          <Chip
            size="small"
            label={`${visibleCount}/${totalFields}`}
            color="primary"
            variant="outlined"
            sx={{ height: 28 }}
          />
        </Tooltip>
      </Paper>

      {/* Settings Panel */}
      <Collapse in={showSettings}>
        <Paper
          sx={{
            maxWidth: containerMaxWidth,
            margin: "auto",
            mb: 2,
            p: 2,
            bgcolor: '#f8f9fa',
            "@media print": { display: "none" },
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle2" fontWeight="600" fontSize="0.85rem">
              Toggle Student Info Fields
            </Typography>
            <Box display="flex" gap={1}>
              <Button size="small" onClick={() => toggleAllFields(true)} sx={{ fontSize: "0.75rem" }}>
                Show All
              </Button>
              <Button size="small" onClick={() => toggleAllFields(false)} sx={{ fontSize: "0.75rem" }}>
                Hide All
              </Button>
            </Box>
          </Box>
          <Grid container spacing={0.5}>
            {Object.entries(STUDENT_INFO_FIELDS).map(([key, field]) => (
              <Grid item xs={12} sm={6} md={4} key={key}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={fieldVisibility[key] || false}
                      onChange={() => toggleFieldVisibility(key)}
                      size="small"
                      color="primary"
                    />
                  }
                  label={
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography variant="caption" fontSize="0.7rem">{field.label}</Typography>
                      {fieldVisibility[key] ? (
                        <VisibilityIcon fontSize="small" color="primary" sx={{ fontSize: 14 }} />
                      ) : (
                        <VisibilityOffIcon fontSize="small" color="disabled" sx={{ fontSize: 14 }} />
                      )}
                    </Box>
                  }
                />
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Collapse>

      {/* Marksheets */}
      {filteredStudents.map((ms, idx) => {
        const studentInfo = getStudentInfo(ms);
        const marks = ms.marks || [];
        const totalMax = marks.reduce((sum, m) => sum + Number(m.maxMark || 0), 0);
        const totalObt = marks.reduce((sum, m) => sum + Number(m.marksObtained || 0), 0);

        const column1Fields = getFieldsByColumn(1);
        const column2Fields = getFieldsByColumn(2);

        return (
          <React.Fragment key={ms.id ?? idx}>
            <Box
              sx={{
                maxWidth: containerMaxWidth,
                margin: "auto",
                px: isLandscape ? 3 : 4,
                pb: isLandscape ? 3 : 4,
                pt: isLandscape ? 1.5 : 2,
                border: `2px solid ${borderColor}`,
                borderRadius: "12px",
                backgroundColor: "#fff",
                backgroundImage: `url(${school?.logoUrl?.replace(/\\/g, "/")})`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",
                backgroundSize: "400px auto",
                backgroundBlendMode: "lighten",
                position: "relative",
              }}
            >
              {/* Header with Logo */}
              <Box
                display="flex"
                alignItems="center"
                justifyContent="flex-start"
                flexDirection="column"
              >
                <img
                  src={school?.logoUrl}
                  alt="School Logo"
                  style={{ width: isLandscape ? 70 : 90, height: isLandscape ? 60 : 80, marginTop: 5 }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <Typography variant={isLandscape ? "h5" : "h4"} sx={{ fontWeight: "bold", color: borderColor }}>
                  {toCamelCase(school?.schoolName || "School Name")}
                </Typography>
                <Typography sx={{ pb: 1, textAlign: "center", fontSize: isLandscape ? "0.75rem" : "0.9rem" }}>
                  {toCamelCase(
                    [
                      school?.addressDTO?.addressLine1,
                      school?.addressDTO?.city,
                      school?.addressDTO?.pincode,
                      school?.addressDTO?.state,
                    ]
                      .filter(Boolean)
                      .join(", ")
                  )}
                </Typography>
                <Typography
                  variant={isLandscape ? "subtitle1" : "h6"}
                  sx={{
                    bgcolor: headerBg,
                    borderRadius: 8,
                    fontWeight: "bold",
                    px: 3,
                    py: 0.5,
                    borderBottom: `3px solid ${borderColor}`,
                    borderLeft: `2px solid ${borderColor}`,
                    borderRight: `1px solid ${borderColor}`,
                    mb: 2,
                    fontSize: isLandscape ? "0.85rem" : "1rem",
                  }}
                >
                  {"MARKS STATEMENT"}
                </Typography>
              </Box>

              {/* Student Info - Two Column Layout */}
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                {fieldVisibility.photo && studentInfo.photo && (
                  <Box my={1} mb={2} position={'absolute'} right={4} top={5}>
                    <img
                      src={studentInfo.photo}
                      alt="Student"
                      style={{
                        width: isLandscape ? 80 : 100,
                        height: isLandscape ? 80 : 100,
                        borderRadius: '5%',
                        objectFit: 'cover',
                        border: `2px solid ${borderColor}`
                      }}
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </Box>
                )}
              </Box>

              <Grid container spacing={isLandscape ? 1 : 3} justifyContent={'space-evenly'} flexWrap={'nowrap'}>
                {/* Column 1 */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    {column1Fields.map(key => renderStudentField(key, studentInfo))}
                  </Box>
                </Grid>

                {/* Column 2 */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    {column2Fields
                      .filter(key => key !== 'photo')
                      .map(key => renderStudentField(key, studentInfo))}
                  </Box>
                </Grid>
              </Grid>

              <Divider sx={{ mb: 2, mt: 2, borderColor }} />

              {/* Exam Type and Academic Year */}
              <Box display="flex" justifyContent="space-between" sx={{ mb: 2 }}>
                {studentInfo.examType && (
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: isLandscape ? "0.85rem" : "1rem" }}>
                    {studentInfo.examType}
                  </Typography>
                )}
                {studentInfo.academicYear && (
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: isLandscape ? "0.85rem" : "1rem" }}>
                    Academic Year: {studentInfo.academicYear}
                  </Typography>
                )}
              </Box>

              {/* Marks Table */}
              <Box sx={{ overflowX: 'auto' }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>#</th>
                      <th style={thStyle}>Subject</th>
                      <th style={thStyle}>Marks Obtained</th>
                      <th style={thStyle}>Max Marks</th>
                      <th style={thStyle}>Grade</th>
                    </tr>
                  </thead>
                  <tbody>
                    {marks.map((m, index) => (
                      <tr key={index}>
                        <td style={{ ...tdStyle, textAlign: "center" }}>{index + 1}</td>
                        <td style={tdStyle}>{toCamelCase(`${m.subjectName}`)}</td>
                        <td style={{ ...tdStyle, textAlign: "center" }}>{m.marksObtained || "-"}</td>
                        <td style={{ ...tdStyle, textAlign: "center" }}>{m.maxMark || "-"}</td>
                        <td style={{ ...tdStyle, textAlign: "center" }}>{m.grade || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: totalsBg, fontWeight: "bold" }}>
                      <td colSpan="2" style={{ ...tdStyle, textAlign: "right" }}>
                        <b>Total</b>
                      </td>
                      <td style={{ ...tdStyle, textAlign: "center" }}>
                        <b>{totalObt}</b>
                      </td>
                      <td style={{ ...tdStyle, textAlign: "center" }}>
                        <b>{totalMax}</b>
                      </td>
                      <td style={{ ...tdStyle, textAlign: "center" }}>
                        <b>{studentInfo.grade}</b>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </Box>

              {/* Summary Section */}
              <Box sx={{ my: 2, display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "space-between" }}>
                <Typography variant="body2" sx={{ fontSize: fontSize }}>
                  <strong>Total Marks:</strong> {studentInfo.totalMarks}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: fontSize }}>
                  <strong>Percentage:</strong> {studentInfo.percentage}%
                </Typography>
                <Typography variant="body2" sx={{ fontSize: fontSize }}>
                  <strong>Grade:</strong> {studentInfo.grade}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: fontSize }}>
                  <strong>Result:</strong>{" "}
                  <span style={{
                    color: studentInfo.result === "PASS" ? "#0904ff" : "#f44336",
                    fontWeight: "bold"
                  }}>
                    {studentInfo.result}
                  </span>
                </Typography>
              </Box>

              {studentInfo.remarks && (
                <Typography variant="body2" color="textSecondary" sx={{ fontSize: fontSize }}>
                  <b>Remarks: </b>
                  {studentInfo.remarks}
                </Typography>
              )}

              {/* Signatures */}
              <Box mt={4} display="flex" justifyContent="space-between" flexWrap="wrap" gap={2}>
                <Typography variant="body2" sx={{ fontSize: fontSize }}>Parent's Signature: _______</Typography>
                <Typography variant="body2" sx={{ fontSize: fontSize }}>Class Teacher Sign.: _______</Typography>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: "bold", fontSize: fontSize }}>
                    Principal/HeadMaster
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: fontSize }}>Seal/Sign.: ________</Typography>
                </Box>
              </Box>

              {/* Watermark for status */}
              {studentInfo.result === "PASS" ? (
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 20,
                    right: 20,
                    opacity: 0.03,
                    transform: "rotate(-15deg)",
                    fontSize: 28,
                    fontWeight: "bold",
                    color: "#0904ff",
                    pointerEvents: "none",
                  }}
                >
                  PASS
                </Box>
              ) : (
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 20,
                    right: 20,
                    opacity: 0.1,
                    transform: "rotate(-15deg)",
                    fontSize: 48,
                    fontWeight: "bold",
                    color: "#f44336",
                    pointerEvents: "none",
                  }}
                >
                  FAIL
                </Box>
              )}
            </Box>

            {/* Page break between students */}
            {idx !== filteredStudents.length - 1 && <div className="pagebreak" />}
          </React.Fragment>
        );
      })}

      {/* Empty state */}
      {filteredStudents.length === 0 && (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography variant="h6" color="textSecondary">
            No marksheet data available for the selected term.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default OneTermMarksheetReceipt;